import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

interface PourOption {
  name: string;
  package_type: string;
  cash_price: number;
  card_price: number;
}

interface TapItem {
  tap_number: number;
  product_name: string;
  brand: string;
  category: string;
  description: string | null;
  keg_size: string;
  oz_remaining: number;
  oz_capacity: number;
  percent_remaining: number;
  tapped_at: string;
  notes: string | null;
  pour_options: PourOption[] | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  craft: "Craft",
  domestic: "Domestic",
  import: "Import",
  seltzer: "Seltzer",
  cider: "Cider",
  rtd: "RTD",
  malternative: "Malt",
  na: "N/A",
};

const POUR_LABELS: Record<string, string> = {
  taster: "Taster",
  pint: "Pint",
  crowler: "Crowler",
  growler: "Growler",
};

export default function TapMenuBoard() {
  const [taps, setTaps] = useState<TapItem[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const poll = useCallback(async () => {
    try {
      const response = await axios.get("/api/tap-menu");
      setTaps(response.data || []);
      setLastUpdate(new Date());
    } catch {
      // silently retry
    }
  }, []);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [poll]);

  // Get unique pour types across all taps for column headers
  const allPourTypes = Array.from(
    new Set(
      taps.flatMap((t) => (t.pour_options || []).map((p) => p.package_type))
    )
  ).sort((a, b) => {
    const order = ["taster", "pint", "crowler", "growler"];
    return order.indexOf(a) - order.indexOf(b);
  });

  if (taps.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: "#1a2129" }}>
        <div className="text-center">
          <img src="/images/logo-primary.jpg" alt="RBW" className="w-64 mx-auto rounded-2xl shadow-2xl mb-8" />
          <p className="text-2xl" style={{ color: "#8B8681" }}>
            Tap menu coming soon
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: "#1a2129" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-10 py-5" style={{ borderBottom: "1px solid rgba(196,191,182,0.15)" }}>
        <div className="flex items-center gap-5">
          <img src="/images/logo-secondary.jpg" alt="RBW" className="h-10 rounded-lg" />
          <div>
            <h1 className="text-2xl font-bold tracking-wide" style={{ color: "#E8E5E0" }}>
              ON TAP
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-8">
          {allPourTypes.length > 0 && (
            <div className="flex gap-6">
              {allPourTypes.map((type) => (
                <div key={type} className="text-center">
                  <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "#8B8681" }}>
                    {POUR_LABELS[type] || type}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-xs" style={{ color: "#8B8681" }}>
            Cash prices shown
          </div>
        </div>
      </div>

      {/* Tap list */}
      <div className="flex-1 overflow-hidden px-10 py-4">
        <div className="h-full flex flex-col justify-evenly">
          {taps.map((tap, idx) => {
            const pours = tap.pour_options || [];

            return (
              <div
                key={tap.tap_number}
                className="flex items-center py-3"
                style={{
                  borderBottom: idx < taps.length - 1 ? "1px solid rgba(196,191,182,0.08)" : "none",
                }}
              >
                {/* Tap number */}
                <div className="w-16 flex-shrink-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                    style={{ background: "#F6BE00", color: "#252d33" }}
                  >
                    {tap.tap_number}
                  </div>
                </div>

                {/* Beer info */}
                <div className="flex-1 min-w-0 mr-6">
                  <div className="flex items-baseline gap-3">
                    <h2 className="text-xl font-bold truncate" style={{ color: "#E8E5E0" }}>
                      {tap.brand || tap.product_name}
                    </h2>
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{
                        background: "rgba(139,184,232,0.15)",
                        color: "#8BB8E8",
                        border: "1px solid rgba(139,184,232,0.25)",
                      }}
                    >
                      {CATEGORY_LABELS[tap.category] || tap.category}
                    </span>
                  </div>
                  {tap.notes && (
                    <p className="text-sm mt-0.5 truncate" style={{ color: "#8B8681" }}>
                      {tap.notes}
                    </p>
                  )}
                </div>

                {/* Pour prices */}
                <div className="flex gap-6 flex-shrink-0">
                  {allPourTypes.map((type) => {
                    const pour = pours.find((p) => p.package_type === type);
                    return (
                      <div key={type} className="w-20 text-center">
                        {pour ? (
                          <div
                            className="text-lg font-bold"
                            style={{ color: "#F6BE00" }}
                          >
                            ${Number(pour.cash_price).toFixed(2)}
                          </div>
                        ) : (
                          <div style={{ color: "#3F5666" }}>-</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Keg level indicator */}
                <div className="w-20 flex-shrink-0 ml-6">
                  <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "rgba(196,191,182,0.15)" }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(3, Number(tap.percent_remaining))}%`,
                        background: Number(tap.percent_remaining) > 50 ? "#2D8A4E"
                          : Number(tap.percent_remaining) > 25 ? "#F6BE00"
                          : Number(tap.percent_remaining) > 10 ? "#D97706"
                          : "#C53030",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between px-10 py-3"
        style={{ borderTop: "1px solid rgba(196,191,182,0.1)" }}
      >
        <div className="flex items-center gap-2">
          <div className="text-xs" style={{ color: "#8B8681" }}>
            Growlers &amp; crowlers filled fresh to go
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs" style={{ color: "#3F5666" }}>
            Cash &amp; debit pricing shown &middot; Card prices slightly higher
          </div>
          <div className="text-[10px]" style={{ color: "#3F5666" }}>
            Updated {lastUpdate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>
    </div>
  );
}
